import { useQuery, useMutation } from "@tanstack/react-query";
import { usePageTracking } from "@/hooks/use-track";
import { HelpTip } from "@/components/help-tip";
import { TutorialTip } from "@/components/TutorialTip";
import { usePageTour } from "@/components/ProductTour";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { getBankCodeLabel, getTierReward, getSignupDiscount } from "@shared/schema";
import { formatMoney, getMarketCurrency } from "@/lib/utils";
import {
  Gift,
  Copy,
  Users,
  PoundSterling,
  Wallet,
  Check,
  Clock,
  XCircle,
  Share2,
  TrendingUp,
  UserCheck,
  Landmark,
  ArrowDownToLine,
  Save,
  Edit2,
  Zap,
  CheckCircle2,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { useState, useEffect } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { slugifyCompanyName, isValidSlugFormat, RESERVED_SLUGS } from "@/lib/slug-constants";
import { Link2 } from "lucide-react";

interface ReferralData {
  referralCode: string;
  trialStatus: string;
  trialEndAt: string | null;
  subscriptionStatus: string;
  referredByUserId: string | null;
  wasReferred: boolean;
  referralDiscountApplied: boolean;
  stats: {
    activeReferrals: number;
    totalReferrals: number;
    monthlyEarnings: string;
    lifetimeEarnings: string;
    walletBalance: string;
  };
  referrals: Array<{
    id: string;
    referredUserId: string;
    status: string;
    startedAt: string;
    activatedAt: string | null;
    expiresAt: string | null;
    endedAt: string | null;
    lastRewardAt: string | null;
    source?: "code" | "link";
    referredUser?: {
      firstName: string | null;
      lastName: string | null;
      subscriptionTier: string | null;
      subscriptionStatus: string | null;
    };
  }>;
}

interface WalletData {
  wallet: {
    balance: string;
    lifetimeEarned: string;
    lifetimePaid: string;
  };
  rewards: Array<{
    id: string;
    referralId: string;
    amount: string;
    billingCycleDate: string;
    status: string;
    createdAt: string;
  }>;
}

interface BankDetails {
  id: string;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  bankName: string | null;
}

interface WithdrawalRequest {
  id: string;
  amount: string;
  status: string;
  accountName: string;
  sortCode: string;
  accountNumber: string;
  bankName: string | null;
  paidAt: string | null;
  rejectedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

function formatCurrency(val: string | number, market?: string | null) {
  const num = typeof val === "string" ? parseFloat(val) : val;
  return formatMoney(isNaN(num) ? 0 : num, market);
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Market-aware display formatter for the user's saved sort/routing code.
// US ABA Routing: 9 digits, single block, NO separator (per US convention on
//                 cheques/statements).
// UK Sort Code:   XX-XX-XX (6 digits) — also used as the FALLBACK formatter for
//                 AU and CA. Why fallback rather than market-specific formatting
//                 for AU XXX-XXX and CA XXXXX-XXX? Two reasons: (a) the existing
//                 backend validation only accepts 6-digit routing for non-US
//                 markets (the AU 6-digit BSB happens to fit; CA's full 8-digit
//                 transit+institution does NOT but that's a pre-existing
//                 limitation outside the US-launch scope), and (b) the contract
//                 of this work is byte-identical UK / AU / CA — the
//                 pre-US-launch formatter rendered AU and CA as XX-XX-XX, so we
//                 keep that. Touching AU / CA formatting would silently change
//                 their UI and risks the byte-identical guarantee.
function formatBankCodeDisplay(code: string, market?: string | null) {
  const clean = code.replace(/[^0-9]/g, "");
  if (market === "us") {
    return clean;
  }
  if (clean.length === 6) return `${clean.slice(0,2)}-${clean.slice(2,4)}-${clean.slice(4,6)}`;
  return clean;
}

// Per-market expected bank-code digit length and account-number digit range.
// Mirrors the server-side validation in `/api/withdrawals/bank-details` so
// the disable-button state is honest about whether the save will succeed.
function getBankFieldSpec(market?: string | null) {
  if (market === "us") {
    return { codeLen: 9, accountMin: 4, accountMax: 17 };
  }
  return { codeLen: 6, accountMin: 8, accountMax: 8 };
}

function maskAccountNumber(num: string) {
  if (num.length <= 4) return num;
  return "****" + num.slice(-4);
}

function StatusBadgeReferral({ status }: { status: string }) {
  const variants: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending: { label: "Pending", variant: "secondary" },
    active: { label: "Active", variant: "default" },
    ended: { label: "Ended", variant: "destructive" },
    fraud: { label: "Flagged", variant: "destructive" },
  };
  const config = variants[status] || { label: status, variant: "outline" as const };
  return <Badge variant={config.variant} data-testid={`badge-referral-status-${status}`}>{config.label}</Badge>;
}

function WithdrawalStatusBadge({ status }: { status: string }) {
  const variants: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    pending: { label: "Pending", variant: "secondary" },
    paid: { label: "Paid", variant: "default" },
    rejected: { label: "Rejected", variant: "destructive" },
  };
  const config = variants[status] || { label: status, variant: "outline" as const };
  return <Badge variant={config.variant} data-testid={`badge-withdrawal-status-${status}`}>{config.label}</Badge>;
}

interface SlugSignup {
  joinedAt: string | null;
  subscriptionStatus: string | null;
  subscriptionTier: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

interface SlugStatsData {
  slug: string | null;
  companyName: string | null;
  stats: {
    thisMonth: { clicks: number; signups: number; paidConversions: number };
    allTime: { clicks: number; signups: number; paidConversions: number };
    chart: { date: string; signups: number }[];
    signups: SlugSignup[];
  } | null;
  history: Array<{ id: string; oldSlug: string; changedAt: string; redirectsUntil: string }>;
}

function SlugSignupChart({ data }: { data: { date: string; signups: number }[] }) {
  const max = Math.max(...data.map(d => d.signups), 1);
  const W = 600;
  const H = 56;
  const pts = data
    .map((d, i) => {
      const x = (i / Math.max(data.length - 1, 1)) * W;
      const y = H - (d.signups / max) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <div className="w-full rounded-md border bg-muted/30 p-2">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 56 }} preserveAspectRatio="none">
        <polyline fill="none" stroke="hsl(var(--primary))" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      </svg>
    </div>
  );
}

function RewardsSection() {
  const { user, refetchUser } = useAuth() as any;
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const partnerSource: string | null = (user as any)?.partnerSource ?? null;
  const partnerCode: string | null = (user as any)?.partnerCode ?? null;
  const subscriptionStatus: string = user?.subscriptionStatus ?? "inactive";
  const partnerRewardClaimed: boolean = (user as any)?.partnerRewardClaimed ?? false;
  const partnerRewardClaimedAt: string | null = (user as any)?.partnerRewardClaimedAt ?? null;

  const claimMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/me/claim-partner-reward", {});
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to claim reward");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Reward marked as claimed" });
      setConfirmOpen(false);
      if (refetchUser) refetchUser();
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const copyCode = () => {
    if (!partnerCode) return;
    navigator.clipboard.writeText(partnerCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (partnerSource !== "structspectrum") return null;

  if (subscriptionStatus !== "active") {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
        <Gift className="h-4 w-4 shrink-0" />
        <span>Your StructSpectrum discount code (50% off a structural calculation) will appear here once your subscription is active.</span>
      </div>
    );
  }

  if (!partnerCode && !partnerRewardClaimed) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span>Generating your StructSpectrum discount code — it will appear here shortly and be emailed to you.</span>
      </div>
    );
  }

  if (partnerRewardClaimed) {
    return (
      <div className="flex items-center gap-3 rounded-lg border px-4 py-3 text-sm">
        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
        <span className="text-muted-foreground">
          StructSpectrum discount used{partnerRewardClaimedAt ? ` — ${new Date(partnerRewardClaimedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}.
        </span>
        {partnerCode && <code className="font-mono text-xs text-muted-foreground ml-1">({partnerCode})</code>}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3">
        <Gift className="h-4 w-4 text-primary shrink-0" />
        <span className="text-sm font-medium">StructSpectrum discount:</span>
        <code className="font-mono text-sm font-bold tracking-wider">{partnerCode}</code>
        <button onClick={copyCode} className="p-1 rounded hover:bg-muted transition-colors" title="Copy code">
          {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
        </button>
        <span className="text-xs text-muted-foreground">50% off a structural calculation</span>
        <a href="https://structspectrum.co.uk" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
          <ExternalLink className="h-3 w-3" />structspectrum.co.uk
        </a>
        <Button size="sm" variant="outline" className="ml-auto text-xs h-7 px-2" onClick={() => setConfirmOpen(true)}>
          <CheckCircle2 className="h-3 w-3 mr-1" /> Mark as used
        </Button>
      </div>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark discount as used?</AlertDialogTitle>
            <AlertDialogDescription>
              This records that you have used your StructSpectrum discount. You can still contact StructSpectrum with your code if you have not done so yet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => claimMutation.mutate()} disabled={claimMutation.isPending}>
              {claimMutation.isPending ? "Saving..." : "Yes, mark as used"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ReferralLinkSection() {
  const { user: currentUser } = useAuth() as any;
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [companyInput, setCompanyInput] = useState("");
  const [availability, setAvailability] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [copiedLink, setCopiedLink] = useState(false);

  const { data: slugData, isLoading: slugLoading } = useQuery<SlugStatsData>({
    queryKey: ["/api/referrals/slug-stats"],
  });

  const hasSlug = !!slugData?.slug;
  const showForm = !hasSlug || isEditing;

  useEffect(() => {
    if (isEditing && slugData) {
      setSlugInput(slugData.slug ?? "");
      setCompanyInput(slugData.companyName ?? "");
      setAvailability(slugData.slug ? "available" : "idle");
    }
    if (!isEditing && !hasSlug) {
      setSlugInput("");
      setCompanyInput("");
      setAvailability("idle");
    }
  }, [isEditing, hasSlug, slugData]);

  useEffect(() => {
    if (showForm && companyInput && !isEditing) {
      setSlugInput(slugifyCompanyName(companyInput));
      setAvailability("idle");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInput]);

  useEffect(() => {
    if (!slugInput) { setAvailability("idle"); return; }
    if (slugInput === slugData?.slug) { setAvailability("available"); return; }
    if (!isValidSlugFormat(slugInput) || RESERVED_SLUGS.has(slugInput)) {
      setAvailability("invalid");
      return;
    }
    setAvailability("checking");
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`/api/referrals/check-slug?slug=${encodeURIComponent(slugInput)}`);
        const d = await r.json();
        setAvailability(d.available ? "available" : "taken");
      } catch {
        setAvailability("idle");
      }
    }, 450);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugInput]);

  const setSlugMutation = useMutation({
    mutationFn: async () => {
      const r = await apiRequest("POST", "/api/referrals/slug", { slug: slugInput, companyName: companyInput });
      if (!r.ok) { const d = await r.json(); throw new Error(d.message || "Failed to save"); }
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Referral link saved" });
      queryClient.invalidateQueries({ queryKey: ["/api/referrals/slug-stats"] });
      setIsEditing(false);
    },
    onError: (err: any) => {
      toast({ title: "Failed to save link", description: err.message, variant: "destructive" });
    },
  });

  const copyLink = () => {
    const url = `https://thepropertool.com/${slugData?.slug}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    toast({ title: "Link copied", description: "Share it with architects, clients, or anyone you want to refer." });
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const canSave = slugInput && companyInput.trim() && availability === "available" && !setSlugMutation.isPending;

  if (slugLoading) {
    return <Card><CardContent className="p-4"><Skeleton className="h-20 w-full" /></CardContent></Card>;
  }

  return (
    <Card data-testid="card-referral-link">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Link2 className="h-5 w-5 shrink-0" />
          Your Referral Link
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!showForm ? (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 bg-muted px-4 py-2 rounded-md flex-1 min-w-0">
                <code className="text-sm font-mono truncate" data-testid="text-referral-link-url">
                  thepropertool.com/{slugData!.slug}
                </code>
              </div>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="button-copy-referral-link">
                {copiedLink ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                {copiedLink ? "Copied" : "Copy Link"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)} data-testid="button-edit-referral-slug">
                <Edit2 className="h-4 w-4 mr-1.5" />
                Edit
              </Button>
            </div>
            {slugData?.companyName && (
              <p className="text-sm text-muted-foreground">
                Shown as: <span className="font-medium text-foreground">{slugData.companyName}</span>
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Share this link on websites, drawings, or emails. Anyone who clicks it gets <strong>{formatMoney(parseFloat(getSignupDiscount(currentUser?.market)), currentUser?.market)} off their first month</strong>, and you earn monthly rewards for up to 12 months.
            </p>

            {slugData?.stats && (
              <div className="space-y-4 pt-2">
                <h3 className="font-medium text-sm">Your Stats</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Metric</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">This month</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">All time</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 text-muted-foreground">Link clicks</td>
                        <td className="py-2 text-right font-semibold" data-testid="text-slug-clicks-month">{slugData.stats.thisMonth.clicks}</td>
                        <td className="py-2 text-right font-semibold" data-testid="text-slug-clicks-total">{slugData.stats.allTime.clicks}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 text-muted-foreground">Signups</td>
                        <td className="py-2 text-right font-semibold" data-testid="text-slug-signups-month">{slugData.stats.thisMonth.signups}</td>
                        <td className="py-2 text-right font-semibold" data-testid="text-slug-signups-total">{slugData.stats.allTime.signups}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-muted-foreground">Active subscribers</td>
                        <td className="py-2 text-right font-semibold" data-testid="text-slug-paid-month">{slugData.stats.thisMonth.paidConversions}</td>
                        <td className="py-2 text-right font-semibold" data-testid="text-slug-paid-total">{slugData.stats.allTime.paidConversions}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {slugData.stats.chart.some(d => d.signups > 0) && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Signups — last 90 days</p>
                    <SlugSignupChart data={slugData.stats.chart} />
                  </div>
                )}
              </div>
            )}

            {slugData?.history && slugData.history.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground font-medium mb-1">Previous links (redirect for 90 days)</p>
                {slugData.history.map(h => (
                  <p key={h.id} className="text-xs text-muted-foreground">
                    /{h.oldSlug} — redirects until {new Date(h.redirectsUntil).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {hasSlug
                ? "Update your company name or change your link slug."
                : "Generate a custom referral link to put on your website, drawings, or anywhere clickable."}
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="referral-company-name">Company or display name</Label>
                <Input
                  id="referral-company-name"
                  placeholder="e.g. Skyhouse Design"
                  value={companyInput}
                  onChange={(e) => {
                    setCompanyInput(e.target.value);
                    if (!hasSlug || !isEditing) {
                      const derived = slugifyCompanyName(e.target.value);
                      if (derived) setSlugInput(derived);
                    }
                  }}
                  data-testid="input-referral-company"
                />
                <p className="text-xs text-muted-foreground">Shown to people who click your link. Required.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="referral-slug-input">Your link</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center rounded-md border border-input overflow-hidden">
                    <span className="px-3 py-2 bg-muted text-muted-foreground text-sm whitespace-nowrap border-r border-input select-none">
                      thepropertool.com/
                    </span>
                    <input
                      id="referral-slug-input"
                      className="px-3 py-2 text-sm bg-background outline-none w-40 min-w-0"
                      placeholder="your-name"
                      value={slugInput}
                      onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                      data-testid="input-referral-slug"
                    />
                  </div>
                  <span className="text-sm">
                    {availability === "checking" && <span className="text-muted-foreground">Checking...</span>}
                    {availability === "available" && <span className="text-green-600 dark:text-green-400 font-medium">Available</span>}
                    {availability === "taken" && <span className="text-destructive font-medium">Already taken</span>}
                    {availability === "invalid" && (
                      <span className="text-destructive font-medium text-xs">
                        {RESERVED_SLUGS.has(slugInput) ? "Reserved word" : "3-30 chars, letters/numbers/hyphens only"}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => setSlugMutation.mutate()}
                  disabled={!canSave}
                  data-testid="button-save-referral-slug"
                >
                  {setSlugMutation.isPending ? "Saving..." : hasSlug ? "Save Changes" : "Generate Link"}
                </Button>
                {hasSlug && (
                  <Button variant="ghost" onClick={() => setIsEditing(false)} data-testid="button-cancel-slug-edit">
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function getReferredDisplayName(referredUser?: { firstName: string | null; lastName: string | null }) {
  if (!referredUser) return "User";
  const name = `${referredUser.firstName || ""} ${referredUser.lastName || ""}`.trim();
  return name || "User";
}

export default function ReferralsPage() {
  usePageTracking("Referrals");
  const { toast } = useToast();
  usePageTour("referrals");
  const { user: currentUser } = useAuth();
  const bankCodeLabel = getBankCodeLabel(currentUser?.market);
  const bankCodePlaceholder = currentUser?.market === "au"
    ? "000-000"
    : currentUser?.market === "ca"
      ? "00000-000" // Canadian transit (5) + institution (3)
      : currentUser?.market === "us"
        ? "123456789" // ABA Routing — 9 digits, no separators
        : "00-00-00";
  const bankSpec = getBankFieldSpec(currentUser?.market);
  const accountPlaceholder = currentUser?.market === "us"
    ? "Account number (4–17 digits)"
    : "Account number";
  const [copied, setCopied] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [bankForm, setBankForm] = useState({ accountName: "", sortCode: "", accountNumber: "", bankName: "" });
  const [withdrawAmount, setWithdrawAmount] = useState("");

  const { data: referralData, isLoading } = useQuery<ReferralData>({
    queryKey: ["/api/referrals/me"],
  });

  const { data: walletData } = useQuery<WalletData>({
    queryKey: ["/api/referrals/wallet"],
  });

  const { data: bankDetails } = useQuery<BankDetails | null>({
    queryKey: ["/api/withdrawals/bank-details"],
  });

  const { data: withdrawals } = useQuery<WithdrawalRequest[]>({
    queryKey: ["/api/withdrawals"],
  });

  const { data: slugSummary } = useQuery<SlugStatsData>({
    queryKey: ["/api/referrals/slug-stats"],
  });

  useEffect(() => {
    if (bankDetails && !editingBank) {
      setBankForm({
        accountName: bankDetails.accountName || "",
        sortCode: formatBankCodeDisplay(bankDetails.sortCode || "", currentUser?.market),
        accountNumber: bankDetails.accountNumber || "",
        bankName: bankDetails.bankName || "",
      });
    }
  }, [bankDetails, editingBank]);

  const saveBankMutation = useMutation({
    mutationFn: async (data: typeof bankForm) => {
      const res = await apiRequest("PUT", "/api/withdrawals/bank-details", data);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Bank details saved", description: "Your bank details have been saved securely." });
      queryClient.invalidateQueries({ queryKey: ["/api/withdrawals/bank-details"] });
      setEditingBank(false);
    },
    onError: (err: any) => {
      toast({ title: "Could not save bank details", description: err.message || "Please check your details and try again.", variant: "destructive" });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async (amount: string) => {
      const res = await apiRequest("POST", "/api/withdrawals/request", { amount });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Withdrawal requested", description: "Your withdrawal request has been submitted. We'll process it shortly." });
      queryClient.invalidateQueries({ queryKey: ["/api/withdrawals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/referrals/wallet"] });
      queryClient.invalidateQueries({ queryKey: ["/api/referrals/me"] });
      setWithdrawAmount("");
    },
    onError: (err: any) => {
      toast({ title: "Withdrawal failed", description: err.message || "Please try again.", variant: "destructive" });
    },
  });

  const copyReferralCode = () => {
    if (!referralData?.referralCode) return;
    const shareUrl = `${window.location.origin}?ref=${referralData.referralCode}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast({ title: "Link copied", description: "Share this link with others to earn rewards." });
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCodeOnly = () => {
    if (!referralData?.referralCode) return;
    navigator.clipboard.writeText(referralData.referralCode);
    toast({ title: "Code copied" });
  };

  // Market-aware live formatter for the routing/sort input.
  // - US ABA Routing: cap at 9 digits, no separators (single block per US convention).
  // - UK Sort Code (also used as fallback for AU + CA — see formatBankCodeDisplay
  //   for the explanation of why AU + CA keep the UK formatter): cap at 6 digits,
  //   format as XX-XX-XX. Byte-identical to the pre-US-launch behaviour for all
  //   three pre-existing markets.
  const handleSortCodeChange = (value: string) => {
    if (currentUser?.market === "us") {
      const digits = value.replace(/[^0-9]/g, "").slice(0, 9);
      setBankForm(prev => ({ ...prev, sortCode: digits }));
      return;
    }
    const digits = value.replace(/[^0-9]/g, "").slice(0, 6);
    let formatted = digits;
    if (digits.length > 4) formatted = `${digits.slice(0,2)}-${digits.slice(2,4)}-${digits.slice(4)}`;
    else if (digits.length > 2) formatted = `${digits.slice(0,2)}-${digits.slice(2)}`;
    setBankForm(prev => ({ ...prev, sortCode: formatted }));
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const stats = referralData?.stats;
  const walletBalance = parseFloat(stats?.walletBalance || "0");
  const trialDaysLeft = referralData?.trialEndAt
    ? Math.max(0, Math.ceil((new Date(referralData.trialEndAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const hasBankDetails = !!bankDetails;
  const showBankForm = editingBank || !hasBankDetails;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-referrals-title">
          Referrals
          <HelpTip testId="button-help-referrals" content={`Share your unique referral code with other builders. When they sign up and subscribe, you earn a fixed monthly amount based on their plan: ${formatMoney(parseFloat(getTierReward("starter", currentUser?.market)), currentUser?.market)} for Lite, ${formatMoney(parseFloat(getTierReward("pro", currentUser?.market)), currentUser?.market)} for Starter, ${formatMoney(parseFloat(getTierReward("team", currentUser?.market)), currentUser?.market)} for Pro, for as long as they stay subscribed. Track your earnings and request withdrawals from this page.`} />
        </h1>
        <p className="text-muted-foreground mt-1">Invite other builders to The Proper Tool and earn a recurring monthly reward for every active subscriber you refer.</p>
      </div>

      <TutorialTip id="referrals-intro" title="Earn Monthly Rewards for Every Referral">
        Share your unique referral link with other builders. When they subscribe to any paid plan, you earn a fixed monthly reward for up to 12 months. The reward amount depends on their plan tier. Add your bank details in the panel below and request a withdrawal once you have reached the minimum amount.
      </TutorialTip>

      {referralData?.trialStatus === "active" && (
        <Card data-testid="card-trial-banner">
          <CardContent className="flex items-center gap-3 p-4">
            <Clock className="h-5 w-5 text-primary shrink-0" />
            <div>
              <span className="font-medium">Free trial active</span>
              <span className="text-muted-foreground ml-2">
                {trialDaysLeft} {trialDaysLeft === 1 ? "day" : "days"} remaining (ends {formatDate(referralData.trialEndAt)})
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <RewardsSection />

      <Card data-testid="card-referral-code">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Share2 className="h-5 w-5 shrink-0" />
            Your Referral Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-muted px-4 py-2 rounded-md flex-1 min-w-0">
              <code className="text-sm font-mono truncate" data-testid="text-referral-code">
                {referralData?.referralCode || "Generating..."}
              </code>
            </div>
            <Button size="sm" variant="outline" onClick={copyCodeOnly} data-testid="button-copy-code">
              {copied ? <Check className="h-4 w-4 mr-1.5 text-green-600" /> : <Copy className="h-4 w-4 mr-1.5" />}
              {copied ? "Copied" : "Copy Code"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Share this with other builders. They type it at signup to get <strong>{formatMoney(parseFloat(getSignupDiscount(currentUser?.market)), currentUser?.market)} off their first month</strong>, and you earn monthly rewards for up to 12 months.
          </p>
          {referralData && (() => {
            const now = new Date();
            // Only count code-originated referrals in this section (link referrals are counted separately below)
            const codeReferrals = (referralData.referrals ?? []).filter(r => r.source === "code");
            const thisMonthSignups = codeReferrals.filter(r => {
              const d = new Date(r.startedAt);
              return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
            }).length;
            const thisMonthActive = codeReferrals.filter(r => {
              if (!r.activatedAt) return false;
              const d = new Date(r.activatedAt);
              return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
            }).length;
            return (
              <div className="space-y-2 pt-1">
                <h3 className="font-medium text-sm">Your Stats</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-muted-foreground">Metric</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">This month</th>
                        <th className="pb-2 font-medium text-muted-foreground text-right">All time</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 text-muted-foreground">Signups</td>
                        <td className="py-2 text-right font-semibold">{thisMonthSignups}</td>
                        <td className="py-2 text-right font-semibold">{codeReferrals.length}</td>
                      </tr>
                      <tr>
                        <td className="py-2 text-muted-foreground">Active subscribers</td>
                        <td className="py-2 text-right font-semibold">{thisMonthActive}</td>
                        <td className="py-2 text-right font-semibold">{referralData.stats?.activeReferrals ?? 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <ReferralLinkSection />

      <div className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Combined Summary</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="card-stat-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                  <p className="text-2xl font-bold" data-testid="text-total-referrals">
                    {referralData?.referrals?.length ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="card-stat-active">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10">
                  <UserCheck className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Referrals</p>
                  <p className="text-2xl font-bold" data-testid="text-active-referrals">
                    {stats?.activeReferrals || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="card-stat-monthly">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10">
                  <TrendingUp className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Earnings</p>
                  <p className="text-2xl font-bold" data-testid="text-monthly-earnings">{formatCurrency(stats?.monthlyEarnings || "0", currentUser?.market)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="card-stat-wallet">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10">
                  <Wallet className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Wallet Balance</p>
                  <p className="text-2xl font-bold" data-testid="text-wallet-balance">{formatCurrency(stats?.walletBalance || "0", currentUser?.market)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="border-primary/40 bg-primary/5 dark:bg-primary/10" data-testid="card-limited-offer">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-primary/15 shrink-0 mt-0.5">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-2">
              <span className="font-semibold text-sm">Monthly Referral Rewards</span>
              <p className="text-sm text-muted-foreground">
                Earn a fixed monthly reward for <strong>12 months</strong> for every subscriber you refer. Your 12-month period starts from the date your referral subscribes. Payments stop automatically once the 12-month period ends or if your referral cancels their subscription.
              </p>
              {(() => {
                const liteAmt = parseFloat(getTierReward("starter", currentUser?.market));
                const starterAmt = parseFloat(getTierReward("pro", currentUser?.market));
                const proAmt = parseFloat(getTierReward("team", currentUser?.market));
                return (
                  <div className="grid grid-cols-3 gap-3 pt-1">
                    <div className="rounded-md border border-border bg-background p-3 text-center" data-testid="card-reward-starter">
                      <p className="text-xs text-muted-foreground mb-1">Lite plan</p>
                      <p className="text-xl font-bold text-primary">{formatMoney(liteAmt, currentUser?.market)}</p>
                      <p className="text-xs text-muted-foreground">per month</p>
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-sm font-bold text-primary">{formatMoney(liteAmt * 12, currentUser?.market)}</p>
                        <p className="text-xs text-muted-foreground">Total per referral</p>
                      </div>
                    </div>
                    <div className="rounded-md border border-primary/40 bg-background p-3 text-center" data-testid="card-reward-pro">
                      <p className="text-xs text-muted-foreground mb-1">Starter plan</p>
                      <p className="text-xl font-bold text-primary">{formatMoney(starterAmt, currentUser?.market)}</p>
                      <p className="text-xs text-muted-foreground">per month</p>
                      <div className="mt-2 pt-2 border-t border-primary/20">
                        <p className="text-sm font-bold text-primary">{formatMoney(starterAmt * 12, currentUser?.market)}</p>
                        <p className="text-xs text-muted-foreground">Total per referral</p>
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-background p-3 text-center" data-testid="card-reward-team">
                      <p className="text-xs text-muted-foreground mb-1">Pro plan</p>
                      <p className="text-xl font-bold text-primary">{formatMoney(proAmt, currentUser?.market)}</p>
                      <p className="text-xs text-muted-foreground">per month</p>
                      <div className="mt-2 pt-2 border-t border-border">
                        <p className="text-sm font-bold text-primary">{formatMoney(proAmt * 12, currentUser?.market)}</p>
                        <p className="text-xs text-muted-foreground">Total per referral</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-withdraw">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Landmark className="h-5 w-5 shrink-0" />
            Withdraw Earnings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-medium text-sm">Bank Details</h3>
              {hasBankDetails && !editingBank && (
                <Button size="sm" variant="ghost" onClick={() => setEditingBank(true)} data-testid="button-edit-bank">
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>

            {showBankForm ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="accountName">Account Name</Label>
                  <Input
                    id="accountName"
                    placeholder="e.g. John Smith"
                    value={bankForm.accountName}
                    onChange={(e) => setBankForm(prev => ({ ...prev, accountName: e.target.value }))}
                    data-testid="input-account-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bankName">Bank Name (optional)</Label>
                  <Input
                    id="bankName"
                    placeholder="e.g. Barclays"
                    value={bankForm.bankName}
                    onChange={(e) => setBankForm(prev => ({ ...prev, bankName: e.target.value }))}
                    data-testid="input-bank-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sortCode">{bankCodeLabel}</Label>
                  <Input
                    id="sortCode"
                    placeholder={bankCodePlaceholder}
                    value={bankForm.sortCode}
                    onChange={(e) => handleSortCodeChange(e.target.value)}
                    // maxLength caps visible chars; needs to be wide enough for
                    // the longest market value WITH separators. UK XX-XX-XX = 8
                    // chars; CA XXXXX-XXX = 9; US 9-digit block = 9. Use 9.
                    maxLength={9}
                    data-testid="input-sort-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accountNumber">Account Number</Label>
                  <Input
                    id="accountNumber"
                    placeholder={accountPlaceholder}
                    value={bankForm.accountNumber}
                    // Cap at the per-market accountMax. UK/AU/CA stay at 8 (the
                    // pre-existing behaviour); US widens to 17 to fit the full
                    // ABA account-number range (varies 4-17 by bank).
                    onChange={(e) => setBankForm(prev => ({ ...prev, accountNumber: e.target.value.replace(/[^0-9]/g, "").slice(0, bankSpec.accountMax) }))}
                    maxLength={bankSpec.accountMax}
                    data-testid="input-account-number"
                  />
                </div>
                <div className="sm:col-span-2 flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={() => saveBankMutation.mutate(bankForm)}
                    // Per-market disable: UK/AU/CA all expect codeLen=6 + account=8
                    // (one branch covers all three since their digit counts collide);
                    // US expects 9-digit routing + account in [accountMin, accountMax].
                    disabled={
                      !bankForm.accountName
                      || bankForm.sortCode.replace(/[^0-9]/g, "").length !== bankSpec.codeLen
                      || bankForm.accountNumber.length < bankSpec.accountMin
                      || bankForm.accountNumber.length > bankSpec.accountMax
                      || saveBankMutation.isPending
                    }
                    data-testid="button-save-bank"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saveBankMutation.isPending ? "Saving..." : "Save Bank Details"}
                  </Button>
                  {editingBank && (
                    <Button variant="ghost" onClick={() => setEditingBank(false)} data-testid="button-cancel-edit-bank">
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-muted rounded-md p-4 space-y-1 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-muted-foreground">Account:</span>
                  <span className="font-medium" data-testid="text-saved-account-name">{bankDetails!.accountName}</span>
                  {bankDetails!.bankName && <span className="text-muted-foreground">({bankDetails!.bankName})</span>}
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <span>
                    <span className="text-muted-foreground">{bankCodeLabel}:</span>{" "}
                    <span className="font-mono" data-testid="text-saved-sort-code">{formatBankCodeDisplay(bankDetails!.sortCode, currentUser?.market)}</span>
                  </span>
                  <span>
                    <span className="text-muted-foreground">Account No:</span>{" "}
                    <span className="font-mono" data-testid="text-saved-account-number">{maskAccountNumber(bankDetails!.accountNumber)}</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="border-t pt-4 space-y-3">
            <h3 className="font-medium text-sm">Request Withdrawal</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">Available:</span>
                <span className="font-semibold" data-testid="text-available-balance">{formatCurrency(walletBalance, currentUser?.market)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">{getMarketCurrency(currentUser?.market).symbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={walletBalance}
                  placeholder="0.00"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className={`${getMarketCurrency(currentUser?.market).symbol.length >= 2 ? "pl-9" : "pl-7"} max-w-[200px]`}
                  data-testid="input-withdraw-amount"
                />
              </div>
              {walletBalance > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setWithdrawAmount(walletBalance.toFixed(2))}
                  data-testid="button-withdraw-all"
                >
                  Withdraw All
                </Button>
              )}
              <Button
                onClick={() => withdrawMutation.mutate(withdrawAmount)}
                disabled={
                  !hasBankDetails ||
                  !withdrawAmount ||
                  parseFloat(withdrawAmount) <= 0 ||
                  parseFloat(withdrawAmount) > walletBalance ||
                  withdrawMutation.isPending
                }
                data-testid="button-submit-withdrawal"
              >
                <ArrowDownToLine className="h-4 w-4 mr-2" />
                {withdrawMutation.isPending ? "Submitting..." : "Request Withdrawal"}
              </Button>
            </div>
            {!hasBankDetails && (
              <p className="text-sm text-muted-foreground">Please save your bank details above before requesting a withdrawal.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {withdrawals && withdrawals.length > 0 && (
        <Card data-testid="card-withdrawal-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <ArrowDownToLine className="h-5 w-5 shrink-0" />
              Withdrawal History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Date</th>
                    <th className="pb-2 font-medium text-muted-foreground">Amount</th>
                    <th className="pb-2 font-medium text-muted-foreground">Account</th>
                    <th className="pb-2 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((w) => (
                    <tr key={w.id} className="border-b last:border-0" data-testid={`row-withdrawal-${w.id}`}>
                      <td className="py-3 text-muted-foreground">{formatDate(w.createdAt)}</td>
                      <td className="py-3 font-medium">{formatCurrency(w.amount, currentUser?.market)}</td>
                      <td className="py-3 text-muted-foreground">
                        {w.accountName} ({formatBankCodeDisplay(w.sortCode, currentUser?.market)}, {maskAccountNumber(w.accountNumber)})
                      </td>
                      <td className="py-3">
                        <WithdrawalStatusBadge status={w.status} />
                        {w.status === "paid" && w.paidAt && (
                          <span className="text-xs text-muted-foreground ml-2">{formatDate(w.paidAt)}</span>
                        )}
                        {w.status === "rejected" && w.rejectionReason && (
                          <span className="text-xs text-destructive ml-2">{w.rejectionReason}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="card-referral-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Users className="h-5 w-5 shrink-0" />
            Your Referrals
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            type UnifiedRow = {
              key: string;
              name: string;
              plan: string | null;
              planStatus: string | null;
              source: "code" | "link";
              referralStatus?: string;
              subStatus?: string;
              joinedAt: string | null;
            };
            const all = (referralData?.referrals ?? []).map(ref => ({
              key: ref.id,
              name: getReferredDisplayName(ref.referredUser),
              plan: ref.referredUser?.subscriptionTier ?? null,
              planStatus: ref.referredUser?.subscriptionStatus ?? null,
              source: (ref.source ?? "code") as "code" | "link",
              referralStatus: ref.status,
              joinedAt: ref.startedAt,
            } as UnifiedRow)).sort((a, b) => {
              const da = a.joinedAt ? new Date(a.joinedAt).getTime() : 0;
              const db = b.joinedAt ? new Date(b.joinedAt).getTime() : 0;
              return db - da;
            });
            if (all.length === 0) {
              return (
                <div className="text-center py-8 text-muted-foreground">
                  <Gift className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No referrals yet</p>
                  <p className="text-sm mt-1">Share your referral code or link to start earning rewards.</p>
                </div>
              );
            }
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Name</th>
                      <th className="pb-2 font-medium text-muted-foreground">Plan</th>
                      <th className="pb-2 font-medium text-muted-foreground">Via</th>
                      <th className="pb-2 font-medium text-muted-foreground">Status</th>
                      <th className="pb-2 font-medium text-muted-foreground">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {all.map(row => {
                      const tierLabel = row.plan ? row.plan.charAt(0).toUpperCase() + row.plan.slice(1) : null;
                      const showPlan = tierLabel && (row.planStatus === "active" || row.planStatus === "trial");
                      return (
                        <tr key={row.key} className="border-b last:border-0" data-testid={`row-referral-${row.key}`}>
                          <td className="py-3 font-medium">{row.name}</td>
                          <td className="py-3 text-muted-foreground">{showPlan ? tierLabel : "—"}</td>
                          <td className="py-3">
                            <Badge variant="outline" className="text-xs">{row.source === "code" ? "Code" : "Link"}</Badge>
                          </td>
                          <td className="py-3">
                            {row.source === "code" ? (
                              <StatusBadgeReferral status={row.referralStatus!} />
                            ) : row.subStatus === "active" ? (
                              <Badge variant="default" className="text-xs">Active</Badge>
                            ) : row.subStatus === "trial" ? (
                              <Badge variant="secondary" className="text-xs">Trial</Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs capitalize">{row.subStatus || "Inactive"}</Badge>
                            )}
                          </td>
                          <td className="py-3 text-muted-foreground whitespace-nowrap">{row.joinedAt ? formatDate(row.joinedAt) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {walletData && walletData.rewards.length > 0 && (
        <Card data-testid="card-reward-history">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <PoundSterling className="h-5 w-5 shrink-0" />
              Reward History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Date</th>
                    <th className="pb-2 font-medium text-muted-foreground">Amount</th>
                    <th className="pb-2 font-medium text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {walletData.rewards.map((reward) => (
                    <tr key={reward.id} className="border-b last:border-0" data-testid={`row-reward-${reward.id}`}>
                      <td className="py-3 text-muted-foreground">{formatDate(reward.createdAt)}</td>
                      <td className="py-3 font-medium">{formatCurrency(reward.amount, currentUser?.market)}</td>
                      <td className="py-3">
                        <Badge variant={reward.status === "credited" ? "default" : "secondary"}>
                          {reward.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t flex items-center justify-between flex-wrap gap-2 text-sm">
              <span className="text-muted-foreground">Lifetime earned: <strong>{formatCurrency(walletData.wallet.lifetimeEarned, currentUser?.market)}</strong></span>
              <span className="text-muted-foreground">Current balance: <strong>{formatCurrency(walletData.wallet.balance, currentUser?.market)}</strong></span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">1</div>
                <h3 className="font-medium">Share Your Code</h3>
              </div>
              <p className="text-sm text-muted-foreground pl-10">Copy your unique referral link and share it with fellow builders. Your code includes your company name for easy recognition.</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">2</div>
                <h3 className="font-medium">They Sign Up</h3>
              </div>
              <p className="text-sm text-muted-foreground pl-10">Your friend gets a 14-day free trial to explore the platform before choosing a plan.</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">3</div>
                <h3 className="font-medium">You Earn a Fixed Reward</h3>
              </div>
              <p className="text-sm text-muted-foreground pl-10">You earn a fixed monthly reward based on their plan: {formatMoney(parseFloat(getTierReward("starter", currentUser?.market)), currentUser?.market)} for Lite, {formatMoney(parseFloat(getTierReward("pro", currentUser?.market)), currentUser?.market)} for Starter or {formatMoney(parseFloat(getTierReward("team", currentUser?.market)), currentUser?.market)} for Pro. Rewards continue for as long as their subscription stays active.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
